import React, { useState, useRef, useCallback, useEffect } from "react";
import { X, CalendarDays } from "lucide-react";
import ModalMod from "../../../../../components/Model_Mod";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ModRuvnlSelectedDate } from "../../../../../store/state/ModRuvnl/ModDate";
import { setModRuvnlSession } from "../../../../../store/state/ModRuvnl/ModSession";
import { setModRuvnlSessionToken } from "../../../../../store/state/ModRuvnl/ModSessionToken";
import { ModLoadDataUpdate } from "../../../../../store/state/ModLoadState";
import {
  fetchgetModRuvnlSession,
  fetchModRuvnlDamDefault,
  fetchModRuvnlGDamDefault,
  fetchModRuvnlRtmDefault,
  fetchModRuvnlDemandDefault,
} from "../../../Utility/RestApi";
import { useMeritEngineNavigation } from "../../useMeritEngineNavigation";
import { ErrorPopup } from "../../../../../components/ErrorHandling";
const CreateNewSessionModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { navigateToPage } = useMeritEngineNavigation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [modSession, setModSession] = useState<number>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [loading, setLoading] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value;
    setSelectedDate(selected);
  };

  const GotoLoadPrice = async () => {
    if (!selectedDate) {
      setError("Please select a date before proceeding.");
      return;
    }

    try {
      setLoading(true);
      const startdate = selectedDate;
      const res = await fetchgetModRuvnlSession(startdate);
      dispatch(ModRuvnlSelectedDate(selectedDate));
      dispatch(setModRuvnlSession(res.session));
      dispatch(setModRuvnlSessionToken(res.session_token));
      if (res) {
        // Fetch all data
        const gdamData = await fetchModRuvnlDamDefault(
          selectedDate,
          res.session
        );
        const damData = await fetchModRuvnlGDamDefault(
          selectedDate,
          res.session
        );
        const rtmData = await fetchModRuvnlRtmDefault(
          selectedDate,
          res.session
        );
        const demandData = await fetchModRuvnlDemandDefault(
          selectedDate,
          res.session
        );
        // Store data in Redux instead of sessionStorage
        dispatch(ModLoadDataUpdate({ type: "gdam", data: gdamData.data }));
        dispatch(ModLoadDataUpdate({ type: "dam", data: damData.data }));
        dispatch(ModLoadDataUpdate({ type: "rtm", data: rtmData.data }));
        dispatch(ModLoadDataUpdate({ type: "demand", data: demandData.data }));

        // Navigate to load-price page using query parameters
        navigateToPage("load-price");
        onClose();
        sessionStorage.removeItem("TabVisited_Ruvnl");
        window.history.replaceState(
          {},
          "",
          window.location.pathname + window.location.search
        );
        // refresh the page to reflect the new session data
      }
    } catch (error) {
      console.log(error);
      setError("Failed to create new session!");
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    dateInputRef.current?.showPicker();
  };

  return (
    <>
      <ModalMod
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setSelectedDate("");
          setLoading(false);
        }}
        width="470px"
        showCloseButton={false}
      >
        {/* Header */}
        <div
          className="px-1 py-1"
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #DFEAF2",
          }}
        >
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-medium"
              style={{
                color: "#525062",
                fontFamily: "Marsden Wide",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "100%",
              }}
            >
              Create New Session
            </h2>
            <button
              onClick={() => {
                onClose();
                setSelectedDate("");
              }}
              className="hover:bg-gray-200 p-1 rounded transition-colors text-[#777777]"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-2 space-y-2">
          {/* Instructions */}
          <div className="mb-5 mt-3 bg-white rounded-lg">
            <h3 className="text-sm font-medium mb-2 text-[#34656D]">
              Instruction to Create MOD
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-sm text-[#8C8A96]">
              <li>Review & Fine-Tune Rates</li>
              <li>Update Resource Availability</li>
              <li>Generate & Review MOD</li>
              <li>View Computed Bids</li>
            </ul>
          </div>

          {/* Date Selector */}
          <div className="space-y-4 border border-[#DFEAF2] p-2 rounded-xl">
            <div>
              <label
                htmlFor="session-date"
                className="block text-sm font-medium mb-1 text-[#6F6D7C]"
              >
                Select The Date
              </label>
              <div
                className="relative flex items-center h-[39px] rounded-lg border"
                style={{ borderColor: "#E2E2E5" }}
              >
                <input
                  id="session-date"
                  type="date"
                  value={dateInputRef.current?.value}
                  onChange={handleDateChange}
                  ref={dateInputRef}
                  onClick={handleClick} // this triggers picker on click anywhere
                  className="w-full pl-3 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-white"
                  style={{
                    color: "#495057",
                    backgroundColor: "#FFFFFF",
                    fontSize: "13px",
                    height: "100%",
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "textfield",
                    backgroundImage: "none",
                    cursor: "pointer", // makes it clear it's clickable
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-4 py-3 flex justify-center bg-white"
          style={{ borderTop: "1px solid #E1E5E9" }}
        >
          <button
            type="submit"
            disabled={loading}
            onClick={() => {
              GotoLoadPrice();
            }}
            className="flex items-center justify-center  w-[165px] h-[40px] border-[0.5px] px-4 py-3 gap-1 rounded-[8px] opacity-100 text-white text-sm font-medium transition-opacity"
            style={{ backgroundColor: "#F1935C" }}
          >
            {loading ? "Loading..." : "Start"}
          </button>
        </div>
        <ErrorPopup error={error} onClose={() => setError(null)} />
      </ModalMod>
    </>
  );
};

export default CreateNewSessionModal;
